import { useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import type { ApexOptions } from "apexcharts";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmt(v: number): string {
  const abs = Math.abs(v);
  const prefix = v < 0 ? "-$" : "$";
  if (abs >= 1000) return `${prefix}${(abs / 1000).toFixed(1)}k`;
  return `${prefix}${abs.toFixed(0)}`;
}

export default function SessionTimeSlotsWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const sessionStats = analyticsData?.sessionStats;
  const buckets = sessionStats?.timeInSessionBuckets ?? [];

  const tableColumns = [
    { key: "label", label: t.analytics.bucketLabel, align: "left" as const },
    { key: "pnl", label: t.analytics.pnlLabel, align: "right" as const },
    { key: "winRate", label: t.analytics.winRateLabel, align: "right" as const },
  ];
  const tableRows = buckets.map((b) => ({
    label: b.label,
    pnl: (
      <span className={b.pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {b.pnl >= 0 ? "+" : ""}
        {b.pnl.toFixed(2)}
      </span>
    ),
    winRate: `${b.winRate.toFixed(1)}%`,
  }));

  const hasData = buckets.some((b) => b.tradeCount > 0);

  const c = chartColors();
  const cAccent = c.accent;
  const cWarn = c.neutral; // muted tone for "below-50" win-rate bars
  const axisLabelStyle = { colors: c.axis, fontSize: "11px" };
  const gridColor = c.grid;
  const tooltipTheme = isDark ? "dark" : "light";

  const pnlOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "bar" as const,
      toolbar: { show: false },
      background: "transparent",
      animations: { enabled: false },
    },
    plotOptions: { bar: { horizontal: false, borderRadius: 3, columnWidth: "55%" } },
    colors: buckets.map((b) => pnlColor(b.pnl)),
    dataLabels: { enabled: false },
    grid: { borderColor: gridColor, strokeDashArray: 3 },
    xaxis: {
      categories: buckets.map((b) => b.label),
      labels: { style: axisLabelStyle },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: axisLabelStyle, formatter: fmt as any } },
    tooltip: { theme: tooltipTheme, y: { formatter: fmt as any } },
    theme: { mode: isDark ? "dark" as const : "light" as const },
  }), [isDark, buckets, gridColor, tooltipTheme, cAccent, cWarn]);

  const winRateOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "bar" as const,
      toolbar: { show: false },
      background: "transparent",
      animations: { enabled: false },
    },
    plotOptions: { bar: { horizontal: false, borderRadius: 3, columnWidth: "55%" } },
    colors: buckets.map((b) => ((b.winRate >= 50) ? cAccent : cWarn)),
    dataLabels: { enabled: false },
    grid: { borderColor: gridColor, strokeDashArray: 3 },
    xaxis: {
      categories: buckets.map((b) => b.label),
      labels: { style: axisLabelStyle },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        style: axisLabelStyle,
        formatter: ((v: number) => `${Math.round(v)}%`) as any,
      },
    },
    tooltip: {
      theme: tooltipTheme,
      y: { formatter: ((v: number) => `${Math.round(v)}%`) as any },
    },
    theme: { mode: isDark ? "dark" as const : "light" as const },
  }), [isDark, buckets, gridColor, tooltipTheme, cAccent, cWarn]);

  if (!analyticsData) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  if (!sessionStats?.hasEnoughData || !hasData) {
    return (
      <Card className="h-full">
        <CardSectionHeader
          label={t.analytics.sessionsLabel ?? t.dashboard.widgets?.sessionTimeSlots ?? "Sessions"}
          title={t.analytics.sessionStatsTimeTitle}
          isDark={isDark}
          inline
        />
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.notEnoughSessionData ?? t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardSectionHeader
        label={t.analytics.sessionsLabel ?? "Sessions"}
        title={t.analytics.sessionStatsTimeTitle}
        isDark={isDark}
        inline
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="px-5 pb-5 pt-1 space-y-4">
        {viewMode === "table" ? (
          <div className="h-[340px]">
            <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
          </div>
        ) : (
        <>
        <div>
          <p className="text-[10px] font-semibold tracking-widest uppercase mb-1 text-muted-foreground">
            {t.analytics.sessionStatsTimeDesc}
          </p>
          <LazyApexChart
            key={`session-time-pnl-${themeId}`}
            options={pnlOptions}
            series={[{ name: "PnL", data: buckets.map((b) => b.pnl) }]}
            type="bar"
            height={180}
          />
        </div>
        <div>
          <p className="text-[10px] font-semibold tracking-widest uppercase mb-1 text-muted-foreground">
            {t.analytics.sessionStatsWinRateTitle}
          </p>
          <LazyApexChart
            key={`session-time-wr-${themeId}`}
            options={winRateOptions}
            series={[{ name: "Win Rate %", data: buckets.map((b) => b.winRate) }]}
            type="bar"
            height={160}
          />
        </div>
        </>
        )}
      </CardContent>
    </Card>
  );
}
